import React, { useState, useCallback, useRef } from 'react';
import ModernToast from './ModernToast';

/**
 * Toast Container & Manager
 * Manages multiple toast notifications
 * 
 * Usage:
 * const toastRef = useRef();
 * toastRef.current.show('Message', 'success');
 * toastRef.current.show('Error!', 'error');
 */

const ToastContainer = React.forwardRef((props, ref) => {
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const show = useCallback((message, type = 'info', duration = 5000, action = null) => {
    const id = toastIdRef.current++;
    const newToast = { id, message, type, duration, action };
    
    setToasts(prev => [...prev, newToast]);
    
    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Expose methods via ref
  React.useImperativeHandle(ref, () => ({
    show,
    dismiss,
    dismissAll,
    success: (msg, duration, action) => show(msg, 'success', duration, action),
    error: (msg, duration, action) => show(msg, 'error', duration, action),
    warning: (msg, duration, action) => show(msg, 'warning', duration, action),
    info: (msg, duration, action) => show(msg, 'info', duration, action),
  }), [show, dismiss, dismissAll]);

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <ModernToast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          action={toast.action}
          onDismiss={() => dismiss(toast.id)}
        />
      ))}
    </div>
  );
});

ToastContainer.displayName = 'ToastContainer';

export default ToastContainer;
